// Dashboard.jsx - CON SISTEMA DE TRIAL Y PLANES

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import PlanModal from './PlanModal';
import PromoPopup from './PromoPopup';
import ConfirmModal from '../components/ConfirmModal';
import TrialBannerFamiliar from '../components/TrialBannerFamiliar';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
axios.defaults.baseURL = API_URL;

function Dashboard() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading, logout, getAccessTokenSilently } = useAuth0();
  
  const [abuelos, setAbuelos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [familiarId, setFamiliarId] = useState(null);
  
  // ⭐ NUEVO: Abuelo seleccionado para upgrade
  const [abueloParaUpgrade, setAbueloParaUpgrade] = useState(null);
  
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Aceptar',
    onConfirm: () => {},
    type: 'info'
  });

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      obtenerDatosFamiliar();
    }
  }, [isLoading, isAuthenticated]);

  const obtenerDatosFamiliar = async () => {
    try {
      const token = await getAccessTokenSilently();
      
      const response = await axios.get('/api/auth/profile', {
        headers: { Authorization: `Bearer ${token}` }
      });

      const familiar = response.data.familiar;
      setFamiliarId(familiar.id);
      
      cargarAbuelos(token);
      
    } catch (error) {
      console.error('Error al obtener datos del familiar:', error);
      
      if (error.response?.status === 404) {
        await registrarFamiliar();
      }
      
      setLoading(false);
    }
  };

  const registrarFamiliar = async () => {
    try {
      const token = await getAccessTokenSilently();
      
      await axios.post('/api/auth/register', {
        email: user.email,
        nombre: user.given_name || user.name?.split(' ')[0] || 'Usuario',
        apellidos: user.family_name || user.name?.split(' ').slice(1).join(' ') || ''
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      obtenerDatosFamiliar();
      
    } catch (error) {
      console.error('Error al registrar familiar:', error);
      setLoading(false);
    }
  };

  const cargarAbuelos = async (token) => {
    try {
      const response = await axios.get(`/api/abuelos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAbuelos(response.data || []);
      
    } catch (error) {
      console.error('Error al cargar abuelos:', error);
      setAbuelos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRegistrarAbuelo = () => {
    setAbueloParaUpgrade(null); // Nuevo abuelo
    setShowPlanModal(true);
  };

  // ⭐ NUEVO: Handler para upgrade de abuelo existente
  const handleUpgradeAbuelo = (abuelo) => {
    setAbueloParaUpgrade(abuelo);
    setShowPlanModal(true);
  };

  const handleSelectPlan = async (plan) => {
    try {
      const token = await getAccessTokenSilently();
      
      const response = await axios.post('/api/stripe/create-checkout-session', {
        priceId: plan.priceId,
        planType: plan.id,
        familiarId: familiarId,
        // ⭐ Si es upgrade, pasar el abueloId
        abueloId: abueloParaUpgrade?.id || null
      }, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
      
    } catch (error) {
      console.error('Error creando checkout session:', error);
      
      setConfirmModal({
        isOpen: true,
        title: '❌ Error al procesar pago',
        message: 'Hubo un problema al procesar el pago. Por favor, intenta de nuevo.',
        type: 'danger',
        confirmText: 'Entendido',
        onConfirm: () => setConfirmModal({ ...confirmModal, isOpen: false })
      });
    }
  };

  const handleCopiarEnlace = async (abuelo) => {
    try {
      if (!abuelo.webapp_link) {
        setConfirmModal({
          isOpen: true,
          title: '⚠️ Link no disponible',
          message: 'Este abuelo aún no tiene un link generado. Intenta recargando la página.',
          type: 'warning',
          confirmText: 'Entendido',
          onConfirm: () => setConfirmModal({ ...confirmModal, isOpen: false })
        });
        return;
      }
      
      let linkToCopy = abuelo.webapp_link;
      if (window.location.hostname === 'localhost') {
        linkToCopy = abuelo.webapp_link.replace('https://paquita.app', 'http://localhost:5173');
      }
      
      await navigator.clipboard.writeText(linkToCopy);
      
      setConfirmModal({
        isOpen: true,
        title: '✅ Link copiado',
        message: `El enlace ha sido copiado al portapapeles.\n\nCompártelo con ${abuelo.nombre} para que pueda hablar con Doña Paquita 💜`,
        type: 'info',
        confirmText: 'Perfecto',
        onConfirm: () => setConfirmModal({ ...confirmModal, isOpen: false })
      });
    } catch (error) {
      console.error('Error al copiar link:', error);
      
      let linkToShow = abuelo.webapp_link;
      if (window.location.hostname === 'localhost') {
        linkToShow = abuelo.webapp_link.replace('https://paquita.app', 'http://localhost:5173');
      }
      
      setConfirmModal({
        isOpen: true,
        title: '📋 Copia el enlace',
        message: linkToShow,
        type: 'info',
        confirmText: 'Cerrar',
        onConfirm: () => setConfirmModal({ ...confirmModal, isOpen: false })
      });
    }
  };

  const handleBorrarAbuelo = (abuelo) => {
    setConfirmModal({
      isOpen: true,
      title: `¿Eliminar a ${abuelo.nombre}?`,
      message: `Esta acción desactivará la cuenta de ${abuelo.nombre} y ya no podrá usar Doña Paquita. Los datos se conservarán pero la cuenta quedará inactiva.`,
      type: 'danger',
      confirmText: 'Sí, eliminar',
      onConfirm: async () => {
        await confirmarBorrarAbuelo(abuelo);
        setConfirmModal({ ...confirmModal, isOpen: false });
      }
    });
  };

  const confirmarBorrarAbuelo = async (abuelo) => {
    try {
      const token = await getAccessTokenSilently();
      
      await axios.put(`/api/abuelos/${abuelo.id}`, 
        { estado: 'pausado' },
        { headers: { Authorization: `Bearer ${token}` } }
  );

      setConfirmModal({
        isOpen: true,
        title: '✅ Abuelo eliminado',
        message: `${abuelo.nombre} ha sido eliminado correctamente.`,
        type: 'info',
        confirmText: 'Entendido',
        onConfirm: () => setConfirmModal({ ...confirmModal, isOpen: false })
      });
      
      cargarAbuelos(await getAccessTokenSilently());
      
    } catch (error) {
      console.error('Error al borrar abuelo:', error);
      
      setConfirmModal({
        isOpen: true,
        title: '❌ Error',
        message: 'No se pudo eliminar el abuelo. Intenta de nuevo.',
        type: 'danger',
        confirmText: 'Entendido',
        onConfirm: () => setConfirmModal({ ...confirmModal, isOpen: false })
      });
    }
  };

  const handleCancelarSuscripcion = async (abuelo) => {
    setConfirmModal({
      isOpen: true,
      title: `¿Cancelar suscripción de ${abuelo.nombre}?`,
      message: `Se cancelará el plan ${abuelo.plan_nombre || "actual"}. El servicio seguirá activo hasta el final del período de facturación.`,
      type: "danger",
      confirmText: "Sí, cancelar",
      onConfirm: async () => {
        try {
          const token = await getAccessTokenSilently();
          await axios.delete(`/api/stripe/subscription/${abuelo.stripe_subscription_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setConfirmModal({
            isOpen: true,
            title: "Suscripción cancelada",
            message: `La suscripción de ${abuelo.nombre} ha sido cancelada.`,
            type: "info",
            confirmText: "Entendido",
            onConfirm: () => { setConfirmModal({ isOpen: false }); window.location.reload(); }
          });
        } catch (error) {
          console.error("Error cancelando:", error);
          setConfirmModal({
            isOpen: true,
            title: "Error",
            message: "No se pudo cancelar la suscripción. Inténtalo de nuevo.",
            type: "danger",
            confirmText: "Cerrar",
            onConfirm: () => setConfirmModal({ isOpen: false })
          });
        }
      }
    });
  };

  const handleEditarAbuelo = (abuelo) => {
    navigate(`/editar-abuelo/${abuelo.id}`);
  };

  const handleLogout = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin
      }
    });
  };

  const cerrarModal = () => {
    setConfirmModal({ ...confirmModal, isOpen: false });
  };

  // ⭐ HELPER: Calcular días de trial restantes
  const getDiasTrialRestantes = (abuelo) => {
    if (!abuelo.fecha_fin_trial || !abuelo.trial_activo) return null;
    const fechaFin = new Date(abuelo.fecha_fin_trial);
    const ahora = new Date();
    const dias = Math.ceil((fechaFin - ahora) / (1000 * 60 * 60 * 24));
    return dias > 0 ? dias : 0;
  };

  // ⭐ HELPER: Obtener color del badge según estado
  const getBadgeEstado = (abuelo) => {
    const diasTrial = getDiasTrialRestantes(abuelo);
    
    if (diasTrial !== null && diasTrial > 0) {
      if (diasTrial <= 2) {
        return { text: `⚠️ ${diasTrial}d trial`, color: 'bg-red-100 text-red-700', urgent: true };
      }
      return { text: `🎁 ${diasTrial}d trial`, color: 'bg-purple-100 text-purple-700', urgent: false };
    }
    
    if (abuelo.subscription_status === 'active') {
      const planColors = {
        basico: 'bg-gray-100 text-gray-700',
        premium: 'bg-purple-100 text-purple-700',
        buddy: 'bg-amber-100 text-amber-700',
      };
      return { 
        text: abuelo.plan_nombre || 'Activo', 
        color: planColors[abuelo.plan_tipo] || 'bg-green-100 text-green-700',
        urgent: false 
      };
    }
    
    return { text: '❌ Inactivo', color: 'bg-red-100 text-red-700', urgent: true };
  };

  if (isLoading || loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner">⏳</div>
        <p>Cargando familiares...</p>
      </div>
  );
  }

  return (
    <div className="dashboard-main">
      <PlanModal
        isOpen={showPlanModal}
        onClose={() => {
          setShowPlanModal(false);
          setAbueloParaUpgrade(null);
        }}
        onSelectPlan={handleSelectPlan}
        currentPlan={abueloParaUpgrade?.plan_tipo}
        abueloNombre={abueloParaUpgrade?.nombre}
      />

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText={confirmModal.confirmText}
        cancelText={confirmModal.type === 'danger' ? 'Cancelar' : undefined}
        onConfirm={confirmModal.onConfirm}
        onCancel={cerrarModal}
      />

      {/* ⭐ BANNER DE TRIAL PARA ABUELOS EN TRIAL */}
      {abuelos.some(a => getDiasTrialRestantes(a) !== null && getDiasTrialRestantes(a) <= 3) && (
        <TrialBannerFamiliar 
          abuelos={abuelos.filter(a => getDiasTrialRestantes(a) !== null && getDiasTrialRestantes(a) <= 3)}
          onUpgradeClick={(abuelo) => handleUpgradeAbuelo(abuelo)}
        />
      )}

      <div className="dashboard-container-main">
        
        <div className="dashboard-header">
          <div>
            <h1>Hola, {user?.name || user?.email}</h1>
            <p className="dashboard-subtitle">Gestiona el cuidado de tus seres queridos</p>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button className="btn-primary" onClick={handleRegistrarAbuelo}>
              + Registrar a tu ser querido
            </button>
            <button onClick={handleLogout} className="btn-logout">
              Cerrar sesión
            </button>
          </div>
        </div>

        <h2 className="section-title">Tus Familiares ({abuelos.length})</h2>

        {abuelos.length === 0 ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '80px 20px',
            textAlign: 'center',
            backgroundColor: '#f9fafb',
            borderRadius: '16px',
            border: '2px dashed #d1d5db',
            minHeight: '400px'
          }}>
            <div style={{
              fontSize: '80px',
              marginBottom: '24px',
              filter: 'grayscale(20%)'
            }}>
              👴👵
            </div>
            <h3 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#111827',
              marginBottom: '12px'
            }}>
              Aún no has registrado ningún abuelo
            </h3>
            <p style={{
              fontSize: '16px',
              color: '#6b7280',
              maxWidth: '500px',
              marginBottom: '32px',
              lineHeight: '1.6'
            }}>
              Comienza registrando a tu familiar para que Doña Paquita pueda empezar a llamarle
            </p>
            <button 
              onClick={handleRegistrarAbuelo}
              style={{
                padding: '16px 32px',
                fontSize: '16px',
                fontWeight: '600',
                color: 'white',
                backgroundColor: '#9333ea',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: '0 4px 6px rgba(147, 51, 234, 0.2)'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#7e22ce';
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 12px rgba(147, 51, 234, 0.3)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = '#9333ea';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(147, 51, 234, 0.2)';
              }}
            >
              + Registrar primer abuelo
            </button>
          </div>
        ) : (
          <div className="abuelos-grid">
            {abuelos.map((abuelo) => {
              const badge = getBadgeEstado(abuelo);
              const diasTrial = getDiasTrialRestantes(abuelo);
              
              return (
                <div key={abuelo.id} className={`abuelo-card-purple ${badge.urgent ? 'ring-2 ring-red-400' : ''}`}>
                  
                  <div className="card-header-purple">
                    <div className="card-profile">
                      <div className="profile-avatar">
                        <img 
                          src={abuelo.foto_url || (abuelo.id % 2 === 0 ? '/paquito.png' : '/paquita.jpg')} 
                          alt={abuelo.nombre} 
                        />
                      </div>
                      <div className="profile-info">
                        <h3 className="profile-name-truncate">{abuelo.nombre} {abuelo.apellidos || ''}</h3>
                        {/* ⭐ BADGE DE ESTADO */}
                        <span className={`profile-badge ${badge.color} px-2 py-0.5 rounded-full text-xs font-semibold`}>
                          {badge.text}
                        </span>
                      </div>
                    </div>
                    <div className="card-actions">
                      <button
                        className="icon-btn"
                        onClick={() => handleEditarAbuelo(abuelo)}
                        title="Editar abuelo"
                      >
                        ✏️
                      </button>
                      <button
                        className="icon-btn"
                        onClick={() => handleBorrarAbuelo(abuelo)}
                        title="Eliminar abuelo"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>

                  <div className="card-body">
                    <div className="card-info-item">
                      <span className="info-icon">📅</span>
                      <span>{abuelo.fecha_nacimiento ? `${new Date().getFullYear() - new Date(abuelo.fecha_nacimiento).getFullYear()} años` : 'Edad no especificada'}</span>
                    </div>
                    <div className="card-info-item">
                      <span className="info-icon">📱</span>
                      <span>{abuelo.telefono}</span>
                    </div>
                    
                    <div className="card-stats">
                      <div className="stat-item">
                        <span className="stat-label">Plan</span>
                        <span className="stat-value">{abuelo.plan_nombre || 'Trial'}</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">Conversaciones</span>
                        <span className="stat-value">{abuelo.total_conversaciones || 0}</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-label">Última actividad</span>
                        <span className="stat-value">
                          {abuelo.ultima_actividad 
                            ? new Date(abuelo.ultima_actividad).toLocaleDateString() 
                            : 'Nunca'}
                        </span>
                      </div>
                    </div>

                    {/* ⭐ ALERTA DE TRIAL EXPIRANDO */}
                    {diasTrial !== null && diasTrial <= 5 && (
                      <div className={`mt-3 p-3 rounded-xl ${diasTrial <= 2 ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'}`}>
                        <p className={`text-sm font-semibold ${diasTrial <= 2 ? 'text-red-700' : 'text-amber-700'}`}>
                          {diasTrial === 0 
                            ? '⚠️ El período de prueba termina HOY'
                            : diasTrial === 1
                            ? '⚠️ El período de prueba termina MAÑANA'
                            : `🎁 ${diasTrial} días de prueba restantes`
                          }
                        </p>
                        <button
                          onClick={() => handleUpgradeAbuelo(abuelo)}
                          className={`mt-2 w-full py-2 rounded-lg text-sm font-bold transition ${
                            diasTrial <= 2 
                              ? 'bg-red-500 text-white hover:bg-red-600' 
                              : 'bg-amber-500 text-white hover:bg-amber-600'
                          }`}
                        >
                          Elegir plan →
                        </button>
                      </div>
                    )}

                    <div className="card-buttons">
                      <button 
                        className="btn-secondary"
                        onClick={() => handleCopiarEnlace(abuelo)}
                      >
                        📋 Copiar enlace
                      </button>
                      <button 
                        className="btn-link"
                        onClick={() => navigate(`/abuelo/${abuelo.id}`)}
                      >
                        📊 Ver reportes
                      </button>
                    </div>

                    {/* ⭐ BOTÓN DE UPGRADE SI YA TIENE PLAN */}
                    {abuelo.subscription_status === 'active' && abuelo.plan_tipo !== 'buddy' && (
                      <button
                        onClick={() => handleUpgradeAbuelo(abuelo)}
                        className="w-full mt-2 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg text-sm font-semibold hover:shadow-lg transition"
                      >
                        ⬆️ Mejorar plan
                      </button>
                    )}

                    {/* BOTÓN CANCELAR SUSCRIPCIÓN */}
                    {abuelo.subscription_status === "active" && abuelo.stripe_subscription_id && (
                      <button
                        onClick={() => handleCancelarSuscripcion(abuelo)}
                        className="btn-cancelar-sub"
                      >
                        Cancelar suscripción
                      </button>
                    )}
                  </div>

                </div>
  );
            })}
          </div>
        )}

      </div>
      {showPlanModal && <PromoPopup onSelectBuddy={() => handleSelectPlan({ id: "buddy", priceId: process.env.REACT_APP_STRIPE_PRICE_BUDDY })} />}
    </div>
  );
}

export default Dashboard;
